/**
 * @fileoverview TipTap List Marker Extension (MOR-977)
 *
 * Round-trips the fixed-width manual list-marker boxes produced by
 * `normalizeManualListMarkers` (see `manual-list-markers.ts` for the why).
 * Each box is an atomic inline node holding the marker text ("(iii)", "a)",
 * "1.") plus the hanging distance its `min-width` reproduces — without this
 * node TipTap would strip the `<span>` on load and the marker would collapse
 * onto the clause text with no gap at all.
 *
 * The node serialises back to the exact `<span data-type="list-marker">`
 * markup it was parsed from, so stored template HTML keeps the fix for the
 * preview, PDF print and generated Day 1 letters; the Word export rewrites
 * the span into a real tab (`listMarkerSpansToWordTabs`).
 */

import { mergeAttributes, Node } from "@tiptap/core";
import { LIST_MARKER_TYPE, listMarkerBoxStyle } from "./manual-list-markers";

/** Fallback hanging distance when a parsed box carries no usable width. */
const DEFAULT_HANG = "36pt";

const MIN_WIDTH_RE =
  /(?:^|;)\s*min-width\s*:\s*(\d*\.?\d+(?:pt|px|pc|in|cm|mm|em|rem)?)\s*(?=;|$)/i;

export const ListMarker = Node.create({
  name: "listMarker",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  // Allow toolbar marks (bold, font size, …) on the marker so it can be
  // formatted with the clause text — leaf inline nodes default to none.
  marks: "_",

  addAttributes() {
    return {
      /** The visible marker text, e.g. `(iii)`. */
      marker: {
        default: "",
        parseHTML: (element: HTMLElement) => element.textContent ?? "",
        // Rendered as the node's text content in renderHTML, not an attribute.
        renderHTML: () => ({}),
      },
      /** Hanging distance the box reproduces (CSS length, e.g. `21.3pt`). */
      hang: {
        default: DEFAULT_HANG,
        parseHTML: (element: HTMLElement) =>
          MIN_WIDTH_RE.exec(element.getAttribute("style") ?? "")?.[1] ??
          DEFAULT_HANG,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${LIST_MARKER_TYPE}"]` }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const hang =
      typeof node.attrs.hang === "string" && node.attrs.hang
        ? node.attrs.hang
        : DEFAULT_HANG;
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-type": LIST_MARKER_TYPE,
        style: listMarkerBoxStyle(hang),
      }),
      String(node.attrs.marker ?? ""),
    ];
  },

  renderText({ node }) {
    // Plain-text extraction (AI chat, search): marker + tab reads naturally.
    return `${String(node.attrs.marker ?? "")}\t`;
  },
});
